import { useEffect, useRef, useState, type KeyboardEvent } from 'react'

import { cn } from '@/lib/utils'

type InlineNumberEditProps = {
  value: number
  onSave: (next: number) => Promise<void>
  min?: number
  step?: number | string
  inputMode?: 'decimal' | 'numeric'
  formatDisplay: (value: number) => string
  ariaLabel: string
  className?: string
  disabled?: boolean
}

export function InlineNumberEdit({
  value,
  onSave,
  min = 0,
  step = 1,
  inputMode = 'numeric',
  formatDisplay,
  ariaLabel,
  className = '',
  disabled = false,
}: InlineNumberEditProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(String(value))
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!editing) setDraft(String(value))
  }, [value, editing])

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  async function commit() {
    if (saving || disabled) return
    const parsed = inputMode === 'decimal' ? Number(draft) : Number.parseInt(draft, 10)
    if (!Number.isFinite(parsed) || parsed < min) {
      setDraft(String(value))
      setEditing(false)
      return
    }
    if (parsed === value) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onSave(parsed)
      setEditing(false)
    } catch {
      setDraft(String(value))
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  function cancel() {
    setDraft(String(value))
    setEditing(false)
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      void commit()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    }
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        inputMode={inputMode}
        min={min}
        step={step}
        value={draft}
        disabled={saving || disabled}
        aria-label={ariaLabel}
        className={cn(
          'w-full min-w-[4.5rem] rounded-lg border border-primary/40 bg-background px-2 py-1 text-sm tabular-nums text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30',
          saving && 'opacity-60',
          className,
        )}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={onKeyDown}
      />
    )
  }

  return (
    <button
      type="button"
      disabled={disabled || saving}
      aria-label={`${ariaLabel}. Click to edit.`}
      className={cn(
        'rounded-lg px-2 py-1 text-left text-sm tabular-nums transition hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30',
        saving && 'opacity-60',
        className,
      )}
      onClick={() => setEditing(true)}
    >
      {formatDisplay(value)}
    </button>
  )
}
